import { useEffect, useState, type JSX } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  api,
  ApiError,
  type AccessHolder,
  type MergeDirectives,
  type MeResponse,
  type ParishSummary,
  type PersonnelData,
  type PersonnelDiff,
  type PersonnelSubmitResult,
} from '../../api-client';
import { RegistrarContact } from '../access/RegistrarContact';
import { PersonnelForm } from './PersonnelForm';
import { PersonnelDiffPreview, allMatchesResolved } from './PersonnelDiffPreview';

interface FrameContext {
  me: MeResponse;
  activeParish: ParishSummary | null;
}

/** The worker's own sentence when it sent one (a scope refusal says what to do), else the raw error. */
function errorText(err: unknown): string {
  if (err instanceof ApiError && err.serverMessage) return err.serverMessage;
  return (err as Error).message;
}

/** Which button failed: the dry-run review, or the save itself. The box is titled by it. */
type FailedStep = 'review' | 'save';

const FAILED_STEP_TITLE: Record<FailedStep, string> = {
  review: "Couldn't review changes.",
  save: 'Not saved.',
};

export function PersonnelPage(): JSX.Element {
  const { activeParish } = useOutletContext<FrameContext>();
  const [data, setData] = useState<PersonnelData | null>(null);
  const [draft, setDraft] = useState<PersonnelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [diff, setDiff] = useState<PersonnelDiff | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<PersonnelSubmitResult | null>(null);
  const [submitError, setSubmitError] = useState<{ step: FailedStep; message: string } | null>(
    null,
  );
  /** Map from change.key → "create_new" | <existing Contact id at THIS parish>. */
  const [mergeChoices, setMergeChoices] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!activeParish) return;
    setLoading(true);
    setError(null);
    setDiff(null);
    setSubmitResult(null);
    api
      .personnel(activeParish.id)
      .then((p) => {
        setData(p);
        setDraft(p);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [activeParish]);

  const reviewChanges = async (): Promise<void> => {
    if (!activeParish || !draft) return;
    setDiffLoading(true);
    setSubmitError(null);
    try {
      const res = await api.personnelDryRun(activeParish.id, draft);
      setDiff(res.diff);
      // Nothing is chosen for the editor, even where there is one same-parish
      // match: linking two records is their decision, and Confirm stays blocked
      // until every match is answered (LeC S4, Miranda J5, cca-0x72d). Matches in
      // another parish carry no Contact id and are never a choice.
      setMergeChoices({});
    } catch (err) {
      setSubmitError({ step: 'review', message: errorText(err) });
    } finally {
      setDiffLoading(false);
    }
  };

  const submitChanges = async (): Promise<void> => {
    if (!activeParish || !draft) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Every answer is sent, `create_new` included: the server blocks a new
      // person with an unanswered same-parish match, so dropping the explicit
      // "someone new" answer made that person impossible to save (cca-0x72d).
      const merges: MergeDirectives = {};
      for (const [key, choice] of Object.entries(mergeChoices)) {
        if (choice) merges[key] = choice;
      }
      const res = await api.personnelSubmit(activeParish.id, draft, merges);
      setSubmitResult(res);
      // Re-fetch so the form reflects the new server state.
      const fresh = await api.personnel(activeParish.id);
      setData(fresh);
      setDraft(fresh);
      setDiff(null);
      setMergeChoices({});
    } catch (err) {
      setSubmitError({ step: 'save', message: errorText(err) });
    } finally {
      setSubmitting(false);
    }
  };

  if (!activeParish) {
    return (
      <div className="rounded-control border border-brand-border bg-white p-6">
        <p className="text-brand-muted">No parish selected.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="panel">
        <p className="text-sm text-ccaanz-grey">Loading personnel for {activeParish.name}…</p>
      </div>
    );
  }

  if (error || !data || !draft) {
    return (
      <div className="rounded-control border border-ccaanz-red/40 bg-ccaanz-red-light px-4 py-3">
        <p className="font-medium text-ccaanz-red">Could not load personnel data.</p>
        <p className="mt-1 text-sm text-ccaanz-red">{error ?? 'Unknown error'}</p>
      </div>
    );
  }

  // Does anything sit between the refusal box and the footer? The review panel
  // or the access list does; with neither, the box is already right above the
  // buttons and a second copy would stack under it as a twin.
  const errorFarFromFooter = diff !== null || activeParish.reason === 'senior_minister';

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-semibold text-ccaanz-black">{data.parish.name}</h2>
        <p className="text-sm text-ccaanz-grey">
          {data.parish.church_type ?? 'Personnel module'}
          {data.parish.church_type ? ' · Personnel' : ''}
        </p>
      </header>

      <PersonnelForm data={draft} onChange={setDraft} parishName={data.parish.name} />

      {submitResult && (
        <div className="rounded-control border border-ccaanz-green/40 bg-ccaanz-green-light px-4 py-3">
          <p className="font-medium text-ccaanz-green">Personnel updated.</p>
          {/* Full green, not /90: 4.56:1 on the green tint where /90 was 3.82:1 (Miranda r2). */}
          <p className="mt-1 text-sm text-ccaanz-green">
            {submitResult.result.rolesAdded} role(s) added, {submitResult.result.rolesRemoved}{' '}
            removed, {submitResult.result.fieldEdits} field edit(s).
            {submitResult.diff.seniorMinisterEdits.length > 0 &&
              ' Senior Minister contact alert sent to the Registrar.'}
          </p>
        </div>
      )}

      {submitError && <SubmitErrorBox failure={submitError} announce />}

      {diff && (
        <PersonnelDiffPreview
          diff={diff}
          mergeChoices={mergeChoices}
          onChoice={(key, choice) => setMergeChoices((prev) => ({ ...prev, [key]: choice }))}
        />
      )}

      {activeParish?.reason === 'senior_minister' && (
        <AccessHoldersPanel parishId={activeParish.id} parishName={activeParish.name} />
      )}

      {/* The same error again, right above the button that was pressed, when
          something stands between it and the first box: on a phone that box
          is then a screen or more away (Miranda r1 S3). Not announced a second
          time — the first box is the alert. */}
      {submitError && errorFarFromFooter && <SubmitErrorBox failure={submitError} />}

      <footer className="flex items-center justify-end gap-3 rounded-control border border-edge bg-paper-cream px-4 py-3">
        {diff ? (
          <>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setDiff(null)}
              disabled={submitting}
            >
              Keep editing
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={submitChanges}
              disabled={submitting || !allMatchesResolved(diff, mergeChoices)}
              title={
                allMatchesResolved(diff, mergeChoices)
                  ? undefined
                  : 'Resolve potential-match decisions above before submitting'
              }
            >
              {submitting ? 'Submitting…' : 'Confirm and submit'}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            onClick={reviewChanges}
            disabled={diffLoading}
          >
            {diffLoading ? 'Computing changes…' : 'Review changes'}
          </button>
        )}
      </footer>
    </div>
  );
}

/**
 * A refused review or save, in the worker's own words, titled by which one
 * failed: a refused review saved nothing and was never a submission (Miranda
 * P-b). `announce` makes it the page's one `role="alert"`, read out the moment
 * it appears. The worker's scope refusal ends "contact the Registrar", so the
 * route to them is in the box. Every line is full red: 4.92:1 on the red tint,
 * where /90 was 4.31:1 (Miranda r2).
 */
function SubmitErrorBox({
  failure,
  announce = false,
}: {
  failure: { step: FailedStep; message: string };
  announce?: boolean;
}): JSX.Element {
  return (
    <div
      role={announce ? 'alert' : undefined}
      className="rounded-control border border-ccaanz-red/40 bg-ccaanz-red-light px-4 py-3"
    >
      <p className="font-medium text-ccaanz-red">{FAILED_STEP_TITLE[failure.step]}</p>
      <p className="mt-1 text-sm text-ccaanz-red">{failure.message}</p>
      <RegistrarContact className="mt-1 text-sm text-ccaanz-red" />
    </div>
  );
}

function AccessHoldersPanel({
  parishId,
  parishName,
}: {
  parishId: string;
  parishName: string;
}): JSX.Element {
  const [holders, setHolders] = useState<AccessHolder[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [revokedIds, setRevokedIds] = useState<Set<string>>(new Set());

  const refresh = (): void => {
    setLoadError(null);
    api
      .accessHolders(parishId)
      .then((r) => setHolders(r.holders))
      .catch((err: Error) => setLoadError(err.message));
  };

  useEffect(() => {
    setHolders(null);
    setRevokedIds(new Set());
    refresh();
  }, [parishId]);

  const visibleHolders = (holders ?? []).filter((h) => !revokedIds.has(h.id));

  const onRevoke = async (holder: AccessHolder): Promise<void> => {
    const displayName =
      [holder.first_name, holder.last_name].filter(Boolean).join(' ') || holder.email || holder.id;
    const reason = window.prompt(
      `Revoke portal access for ${displayName}?\n\nOptional reason (will be shown in the audit log and the notification email):`,
      '',
    );
    if (reason === null) return; // user cancelled
    setRevokeError(null);
    setRevokingId(holder.id);
    try {
      await api.revokeAccess(parishId, holder.id, reason.trim() || undefined);
      setRevokedIds((prev) => new Set(prev).add(holder.id));
    } catch (err) {
      setRevokeError((err as Error).message);
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <section className="panel">
      <h3 className="section-heading">Manage access — {parishName}</h3>
      <p className="section-sub">
        Anyone listed here can sign in and edit {parishName}'s personnel records. Revoke access if
        someone has left the parish or no longer needs to maintain these records. Your own access
        (as Senior Minister) is granted automatically and isn't listed.
      </p>
      {loadError && (
        <p className="text-sm text-ccaanz-red">Couldn't load access holders: {loadError}</p>
      )}
      {revokeError && <p className="text-sm text-ccaanz-red">Revoke failed: {revokeError}</p>}
      {holders === null && !loadError && <p className="text-sm text-ccaanz-grey">Loading…</p>}
      {holders !== null && visibleHolders.length === 0 && (
        <p className="text-sm text-ccaanz-grey">
          No one else currently has portal access at {parishName}.
        </p>
      )}
      {visibleHolders.length > 0 && (
        <ul className="space-y-2">
          {visibleHolders.map((h) => {
            const name = [h.first_name, h.last_name].filter(Boolean).join(' ') || '(no name)';
            return (
              <li
                key={h.id}
                className="flex items-center justify-between gap-3 rounded-control border border-edge bg-white px-3 py-2"
              >
                <div className="text-sm">
                  <p className="font-medium">{name}</p>
                  <p className="text-ccaanz-grey">{h.email ?? '(no email)'}</p>
                  {h.role.length > 0 && (
                    <p className="text-xs text-ccaanz-grey">Role: {h.role.join(', ')}</p>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-ghost text-ccaanz-red"
                  onClick={() => onRevoke(h)}
                  disabled={revokingId === h.id}
                >
                  {revokingId === h.id ? 'Revoking…' : 'Revoke access'}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
